import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  Container,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('dealeros_remember_me') === 'true';
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);

      if (rememberMe) {
        const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
        localStorage.setItem('dealeros_remember_me', 'true');
        localStorage.setItem('dealeros_session_expiry', String(expiry));
      } else {
        localStorage.removeItem('dealeros_remember_me');
        localStorage.removeItem('dealeros_session_expiry');
      }

      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: '#000',
        backgroundImage: `
          radial-gradient(ellipse 80% 60% at 50% 0%, rgba(30, 41, 59, 0.8) 0%, transparent 60%),
          radial-gradient(ellipse 60% 50% at 50% 100%, rgba(15, 23, 42, 0.9) 0%, transparent 60%),
          linear-gradient(180deg, #000 0%, #0a0a0a 100%)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80%',
          height: '80%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 60%)',
          filter: 'blur(100px)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            borderRadius: 4,
            background: 'rgba(10, 10, 12, 0.72)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: `
              0 0 0 1px rgba(255, 255, 255, 0.04) inset,
              0 1px 0 0 rgba(255, 255, 255, 0.08) inset,
              0 30px 60px -15px rgba(0, 0, 0, 0.8),
              0 0 100px -20px rgba(59, 130, 246, 0.15)
            `,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <img
              src="/DealerOS - Icon.svg"
              alt="DealerOS Icon"
              style={{
                height: '60px',
                width: '60px',
                marginBottom: '16px',
                filter: 'drop-shadow(0 4px 20px rgba(255, 255, 255, 0.15))',
              }}
            />
            <Typography component="h1" variant="h4" align="center" sx={{ color: '#fff', fontWeight: 600 }}>
              Dealer<Box component="span" sx={{ fontWeight: 800 }}>OS</Box>
            </Typography>
          </Box>

          <Typography variant="body2" align="center" sx={{ mb: 3, color: 'rgba(255,255,255,0.6)' }}>
            Sign in to access your dealership management system
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              '& .MuiTextField-root .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: 'rgba(255,255,255,0.04)',
                transition: 'all 0.2s ease',
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.15)',
                  borderWidth: '1.5px',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255,255,255,0.6)',
                  borderWidth: '1.5px',
                  boxShadow: '0 0 0 4px rgba(255,255,255,0.08)',
                },
                '& input': { color: '#fff' },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px rgba(15,15,18,1) inset',
                  WebkitTextFillColor: '#fff',
                  caretColor: '#fff',
                },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255,255,255,0.9)' },
              '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.6)' },
              '& .MuiCheckbox-root': { color: 'rgba(255,255,255,0.4)' },
              '& .MuiCheckbox-root.Mui-checked': { color: '#fff' },
            }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label={<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>Remember me for 30 days</Typography>}
              />
              <Link to="/forgot-password" style={{ textDecoration: 'none', fontSize: '0.875rem', color: '#60a5fa' }}>
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                mb: 2,
                py: 1.4,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                background: 'linear-gradient(180deg, #ffffff 0%, #e5e5e7 100%)',
                color: '#000',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: `
                  0 1px 0 0 rgba(255,255,255,0.6) inset,
                  0 0 0 1px rgba(0,0,0,0.2),
                  0 10px 30px -10px rgba(0,0,0,0.6)
                `,
                '&:hover': {
                  background: 'linear-gradient(180deg, #ffffff 0%, #d4d4d8 100%)',
                  boxShadow: `
                    0 1px 0 0 rgba(255,255,255,0.8) inset,
                    0 0 0 1px rgba(0,0,0,0.2),
                    0 15px 35px -10px rgba(0,0,0,0.7)
                  `,
                },
                '&.Mui-disabled': {
                  background: 'rgba(255,255,255,0.3)',
                  color: 'rgba(0,0,0,0.5)',
                },
              }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Divider sx={{ my: 2, '&::before, &::after': { borderColor: 'rgba(255,255,255,0.1)' } }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>OR</Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Don't have an account?{' '}
                <Link to="/sign-up" style={{ textDecoration: 'none', color: '#60a5fa', fontWeight: 500 }}>
                  Sign up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
